import { Injectable } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@client/app/services/translate/translate.service';
import {
    STATISTICS_DATE_RANGE_PRESETS,
    StatisticsDateRangePreset,
    StatisticsDateRangeValue,
} from './statistics-date-range.presets';

export type StatisticsDatePickerOptions = {
    ranges: Record<string, [moment.Moment, moment.Moment]>;
};

export type StatisticsTranslatedDateRangePreset = {
    label: string;
    getRange: () => StatisticsDateRangeValue;
};

@Injectable({
    providedIn: 'root',
})
export class StatisticsDateRangeService {
    readonly presets: StatisticsDateRangePreset[] = STATISTICS_DATE_RANGE_PRESETS;

    private lastSelectedRange: StatisticsDateRangeValue | null = null;

    constructor (
        private translateService: TranslateService,
    ) {}

    /**
     * Remembers the range currently applied on a statistics screen, so it
     * can be restored when navigating to another screen in the module (the
     * navigation links do not carry the from/to params over).
     * Clones are stored and returned to protect against mutation by the
     * date pickers.
     */
    setLastSelectedRange (range: StatisticsDateRangeValue): void {
        this.lastSelectedRange = {
            startDate: moment(range.startDate),
            endDate: moment(range.endDate),
        };
    }

    getLastSelectedRange (): StatisticsDateRangeValue | null {
        if (!this.lastSelectedRange) {
            return null;
        }

        return {
            startDate: moment(this.lastSelectedRange.startDate),
            endDate: moment(this.lastSelectedRange.endDate),
        };
    }

    /**
     * Default range applied when no valid from/to params are present.
     * Matches the old UI: the last 7 days ending yesterday.
     */
    getDefaultRange (): StatisticsDateRangeValue {
        return {
            startDate: moment().subtract(7, 'days'),
            endDate: moment().subtract(1, 'days').endOf('day'),
        };
    }

    /**
     * Presets with translated labels for pickers that render their own
     * quick-select UI (e.g. the PrimeNG based date range pickers). The
     * ranges are evaluated lazily, so they stay relative to the moment of
     * selection.
     */
    getTranslatedPresets (): StatisticsTranslatedDateRangePreset[] {
        return this.presets.map((preset) => ({
            label: this.translateService.getString(preset.labelKey),
            getRange: () => preset.getRange(),
        }));
    }

    /**
     * Builds the date-picker options that make the default time range
     * selections (Last Week, Last Month, Last Quarter, Last Half Year and
     * Last Year) visible in the picker, like in the old UI.
     *
     * Build this once and reuse the returned object: the underlying
     * daterangepicker re-renders whenever the options values change.
     */
    getDatePickerOptions (): StatisticsDatePickerOptions {
        const ranges: Record<string, [moment.Moment, moment.Moment]> = {};

        this.presets.forEach((preset) => {
            const { startDate, endDate } = preset.getRange();
            ranges[this.translateService.getString(preset.labelKey)] = [startDate, endDate];
        });

        return { ranges };
    }
}
